import { ref, computed, onMounted, onUnmounted } from 'vue'

export interface Shortcut {
  id: string
  key: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  meta?: boolean
  global?: boolean
  handler: () => void
  description: string
  category?: string
}

// Singleton registry — shared across all consumers
const registry = ref<Map<string, Shortcut>>(new Map())

// Sequence state for multi-key shortcuts (e.g., "G then T")
let pendingPrefix: string | null = null
let pendingTimeout: ReturnType<typeof setTimeout> | null = null

function isInputFocused(): boolean {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if ((el as HTMLElement).isContentEditable) return true
  return false
}

function buildId(shortcut: Pick<Shortcut, 'key' | 'ctrl' | 'shift' | 'alt'>): string {
  const parts: string[] = []
  if (shortcut.ctrl) parts.push('ctrl')
  if (shortcut.shift) parts.push('shift')
  if (shortcut.alt) parts.push('alt')
  parts.push(shortcut.key.toLowerCase())
  return parts.join('+')
}

function handleKeydown(e: KeyboardEvent): void {
  const inInput = isInputFocused()

  const ctrl = e.metaKey || e.ctrlKey
  const shift = e.shiftKey
  const alt = e.altKey
  const key = e.key.toLowerCase()

  // Build lookup id for this keystroke
  const id = buildId({ key, ctrl, shift, alt })

  // Check for global shortcuts first (work even in inputs)
  if (inInput) {
    const globalShortcut = registry.value.get(id)
    if (globalShortcut?.global) {
      e.preventDefault()
      globalShortcut.handler()
      return
    }
    pendingPrefix = null
    return
  }

  // Check for second key of a sequence (e.g., pending "g" + this "t" = "g t")
  if (pendingPrefix) {
    const seqId = `${pendingPrefix} ${id}`
    const seqShortcut = registry.value.get(seqId)
    pendingPrefix = null
    if (pendingTimeout) {
      clearTimeout(pendingTimeout)
      pendingTimeout = null
    }
    if (seqShortcut) {
      e.preventDefault()
      seqShortcut.handler()
      return
    }
  }

  // Check for direct match
  const shortcut = registry.value.get(id)
  if (shortcut) {
    e.preventDefault()
    shortcut.handler()
    return
  }

  // Check if this keystroke is the prefix of any sequence shortcut
  const isPrefix = Array.from(registry.value.keys()).some(
    registeredId => registeredId.startsWith(id + ' ')
  )
  if (isPrefix) {
    pendingPrefix = id
    // Clear pending after 1 second if no follow-up key
    pendingTimeout = setTimeout(() => {
      pendingPrefix = null
      pendingTimeout = null
    }, 1000)
  }
}

// Track listener count so we only bind once globally
let listenerCount = 0

export function useKeyboardShortcuts() {
  const localIds = ref<string[]>([])

  function register(shortcuts: Omit<Shortcut, 'id'>[]): void {
    for (const s of shortcuts) {
      const id = s.key.includes(' ')
        ? s.key // Allow pre-formatted sequence ids like "g t"
        : buildId(s)
      const full: Shortcut = { ...s, id }
      registry.value.set(id, full)
      localIds.value.push(id)
    }
  }

  function unregister(ids?: string[]): void {
    const toRemove = ids || localIds.value
    for (const id of toRemove) {
      registry.value.delete(id)
    }
    if (!ids) {
      localIds.value = []
    }
  }

  const allShortcuts = computed(() => Array.from(registry.value.values()))

  onMounted(() => {
    listenerCount++
    if (listenerCount === 1) {
      document.addEventListener('keydown', handleKeydown)
    }
  })

  onUnmounted(() => {
    // Remove shortcuts registered by this instance
    unregister()
    listenerCount--
    if (listenerCount === 0) {
      document.removeEventListener('keydown', handleKeydown)
    }
  })

  return { register, unregister, allShortcuts }
}
